import DOMPurify from "isomorphic-dompurify";
import {
  useMemo,
  type ComponentProps,
  type ReactNode,
  type ElementType,
} from "react";
import { type FormattedMessage, useIntl } from "react-intl";
import style from "./FormattedHTMLMessage.module.scss";

// This component recreates deprecated FormattedHTMLMessage to make writing translations easier
// and backward compatibile. It also sanitize output for security concerns.
// https://formatjs.github.io/docs/react-intl/components#rich-text-formatting
export const FormattedHTMLMessage = ({
  id,
  values,
  Element,
}: Pick<ComponentProps<typeof FormattedMessage>, "id"> & {
  values?: Record<string, string>;
  Element?: ElementType;
}): ReactNode => {
  const intl = useIntl();
  const message = intl.formatMessage({ id });
  const Wrapper: ElementType = Element || "span";

  const html = useMemo(() => {
    let htmlMessage = message;

    if (values) {
      Object.keys(values).forEach((key) => {
        const r = new RegExp(`{${key}}`, "g");

        htmlMessage = htmlMessage.replaceAll(r, values[key]);
      });
    }

    return DOMPurify.sanitize(htmlMessage, {
      ADD_ATTR: ["target"],
    });
  }, [message, values]);

  return (
    <Wrapper
      dangerouslySetInnerHTML={{
        __html: html,
      }}
      className={Element ? undefined : style.message}
    />
  );
};
